import { getAffiliateIdFromStorage } from '@repo/affiliate-id'
import { usePaymentsAutoStartSubscriptionMutation } from '@repo/studio-hooks-coins/gen/coins'
import { usePricesForUser } from '@repo/cf-user/hooks/use-prices'
import type { CoinSubscriptionPlan, DefaultPlan } from 'payments/payments-dialog/lib/plans'
import {
  subscriptionRequiresPaymentRecovery,
  tryOpenPaymentRequiredDialogFromSubscription,
} from '@repo/studio-hooks-payments/use-payment-required-dialog'
import {
  COIN_PLAN_TO_SUBSCRIPTION_PLAN,
  SUBSCRIPTION_PLAN_TO_COIN_PLAN,
  getTierForCoinPlan,
  toAvailableCurrencyCode,
} from '@repo/studio-hooks-coins/constants/coin-top-up'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'

export function useAutostartCoinPlan(
  onFallbackToCheckout: (plan: DefaultPlan) => void,
  onSuccess?: () => void,
) {
  const { currency } = usePricesForUser()
  const autostartMutation = usePaymentsAutoStartSubscriptionMutation()

  const autostartPlan = autostartMutation.variables?.input.subscriptionPlan
  const autostartSubscription = autostartMutation.data?.paymentsAutoStartSubscription.subscription
  // A `requires_action` / failed invoice still resolves the mutation with a
  // non-null subscription; gate `successPlan` on actual payment success so the
  // top-up dialog stays open behind the recovery dialog stacked on top.
  const succeeded =
    autostartMutation.isSuccess &&
    !autostartMutation.data?.paymentsAutoStartSubscription.errors?.length &&
    autostartSubscription !== null &&
    !subscriptionRequiresPaymentRecovery(autostartSubscription)

  const successPlan: DefaultPlan | null =
    succeeded && autostartPlan ? (SUBSCRIPTION_PLAN_TO_COIN_PLAN[autostartPlan] ?? null) : null

  function startPlan(plan: CoinSubscriptionPlan) {
    autostartMutation.mutate(
      {
        input: {
          subscriptionPlan: COIN_PLAN_TO_SUBSCRIPTION_PLAN[plan],
          currencyCode: toAvailableCurrencyCode(currency.code),
          isTrial: false,
          // Forward the captured ref for apps that can't share the affwp_ref cookie
          // cross-domain. On cookie-bearing domains BE reads the cookie directly.
          affiliateId: getAffiliateIdFromStorage() ?? undefined,
        },
      },
      {
        onSuccess(data) {
          const { errors, subscription } = data.paymentsAutoStartSubscription

          if (errors?.length || !subscription) {
            onFallbackToCheckout(plan)
            return
          }

          if (tryOpenPaymentRequiredDialogFromSubscription(subscription)) {
            return
          }

          const tier = getTierForCoinPlan(plan)
          if (tier) {
            sendAmplitudeEvent(AmplitudeEvents.PURCHASED_COIN_PACK, {
              coin_pack_type: 'subscription',
              coin_pack: `${tier.coins} coins`,
              price: tier.priceUsd,
            })
          }
          onSuccess?.()
        },
        onError() {
          onFallbackToCheckout(plan)
        },
      },
    )
  }

  return { startPlan, successPlan, reset: autostartMutation.reset }
}
